import React from 'react';
import { ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

const FAQ = [
  {
    q: 'What format will I receive the book in?',
    a: "You'll receive a high-quality PDF (readable on phone, tablet, laptop, or Kindle) delivered straight to your email immediately after payment.",
  },
  {
    q: "What are the bonuses I'll receive?",
    a: 'You get 6 free bonuses worth ₹12,000: The Gratitude Journal, Ho’oponopono Vision Board, HRCM Vision Board, Love Letter to Money, Money Manifestation Vision Board, and the Theta Visualisation Audio.',
  },
  {
    q: 'Is this book for beginners or advanced readers?',
    a: "Both. The principles are explained from first principles — you don't need any prior background. At the same time, the assignments are deep enough to challenge experienced practitioners.",
  },
  {
    q: 'How long does it take to finish?',
    a: 'Most readers complete the book in 4–6 hours of focused reading. But this is a working book — the assignments are designed to be done over 2–4 weeks for real shifts.',
  },
  {
    q: 'What payment methods do you support?',
    a: 'Secure checkout via Razorpay — UPI, all major debit/credit cards, net banking and wallets.',
  },
  {
    q: 'Can I get a refund?',
    a: 'Yes. We offer a 7-day “shift or refund” guarantee. If the book doesn’t serve you, email info@nishitthakkar.in within 7 days for a full refund.',
  },
];

export default function BookFinale({ paymentLink }) {
  return (
    <>
      {/* Decide */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5 text-center">
            Decide Now
          </p>
          <h2 className="font-serif text-center text-white text-[32px] md:text-[48px] leading-[1.05] tracking-tight">
            Two paths leave this page.
            <br />
            <span className="italic text-amber-200">Only one rewrites your life.</span>
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-white/10 bg-navy-900/40 p-7 md:p-8">
              <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">Path One</p>
              <p className="mt-3 text-neutral-300 text-[14.5px] leading-relaxed">
                Close this tab. Wake up tomorrow inside the same money story. Six months from now,
                nothing has changed.
              </p>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-amber-300/30 bg-gradient-to-br from-amber-300/10 via-amber-400/5 to-transparent p-7 md:p-8">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-amber-300/15 blur-3xl" />
              <div className="relative">
                <p className="text-[11px] tracking-[0.22em] uppercase text-amber-300">Path Two</p>
                <p className="mt-3 text-neutral-200 text-[14.5px] leading-relaxed">
                  Claim the book + 6 bonuses for ₹399 (less than a dinner out). Read it tonight.
                  Apply assignment one tomorrow. Watch the shift begin within 7 days.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <div className="inline-flex items-baseline gap-3">
              <span className="font-serif text-neutral-500 text-[22px] md:text-[28px] line-through">
                ₹12,499
              </span>
              <span className="font-serif bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent text-[44px] md:text-[56px] leading-none">
                ₹399
              </span>
            </div>
            <p className="mt-2 text-[11px] tracking-[0.22em] uppercase text-amber-300/80">
              Book + ₹12,000 in Bonuses · Today Only at Launch Price
            </p>
            <div className="mt-7">
              <a
                href={paymentLink}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-[15px] font-semibold text-navy-900 bg-gradient-to-b from-amber-200 to-amber-400 shadow-[0_10px_36px_-6px_rgba(251,191,36,0.55)] hover:-translate-y-[2px] hover:shadow-[0_16px_44px_-6px_rgba(251,191,36,0.75)] transition-all"
              >
                Yes — I Choose Path Two
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            <p className="mt-4 text-[12px] text-neutral-500">
              7-day refund promise · Instant delivery · Secure checkout
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5 text-center">
            Questions
          </p>
          <h2 className="font-serif text-center text-white text-[32px] md:text-[48px] leading-[1.05] tracking-tight">
            Before you <span className="italic text-amber-200">begin.</span>
          </h2>

          <Accordion type="single" collapsible className="mt-10 rounded-2xl border border-white/10 bg-navy-900/40 overflow-hidden">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className={i < FAQ.length - 1 ? 'border-b border-white/5' : 'border-0'}
              >
                <AccordionTrigger className="px-6 py-5 md:px-8 md:py-6 text-left text-white text-[15px] md:text-[16px] hover:no-underline hover:text-amber-200 font-serif">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 md:px-8 md:pb-6 text-neutral-400 text-[14px] leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Last call */}
      <section className="relative py-20 md:py-28 border-t border-white/5 overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-amber-300/[0.07] blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5">
            Last Call
          </p>
          <h2 className="font-serif text-white text-[36px] md:text-[56px] leading-[1.05] tracking-tight">
            Ready to stop chasing wealth
            <br />
            and <span className="italic text-amber-200">start creating it?</span>
          </h2>
          <p className="mt-6 text-neutral-400 text-[15px] md:text-[17px] leading-relaxed max-w-xl mx-auto">
            Step into the version of you that doesn't wait for abundance — but generates it.
          </p>
          <div className="mt-8">
            <a
              href={paymentLink}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-[15px] font-semibold text-navy-900 bg-gradient-to-b from-amber-200 to-amber-400 shadow-[0_10px_36px_-6px_rgba(251,191,36,0.55)] hover:-translate-y-[2px] hover:shadow-[0_16px_44px_-6px_rgba(251,191,36,0.75)] transition-all"
            >
              Get Instant Access — ₹399
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
          <p className="mt-4 text-[12px] text-neutral-500">
            7-Day refund promise · Instant PDF delivery
          </p>
        </div>
      </section>
    </>
  );
}
